import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Download, Loader2, CheckCircle } from 'lucide-react';
import { guideTexts } from '../data/guideTexts';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { first_name: '', last_name: '', email: '', phone: '', website: '', reason: '', hp: '' };

const triggerDownload = (url) => {
  const a = document.createElement('a');
  a.href = `${API}${url}`;
  a.setAttribute('download', '');
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
    {children}
  </div>
);

const GuideDownloadForm = ({ location }) => {
  const { language } = useLanguage();
  const g = guideTexts[language] || guideTexts.es;
  const [form, setForm] = useState(EMPTY);
  const [pdfLang, setPdfLang] = useState(language === 'fr' ? 'fr' : 'es');
  useEffect(() => { setPdfLang(language === 'fr' ? 'fr' : 'es'); }, [language]);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle');
  const [downloadUrl, setDownloadUrl] = useState('');
  const uid = (name) => `guide-${location}-${name}`;

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const r = await fetch(`${API}/api/guide/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, language, pdf_lang: pdfLang, page: window.location.pathname }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      window.dataLayer?.push({ event: 'guide_download', form_name: 'guide_suspended_account', click_location: location, pdf_lang: pdfLang, language });
      setDownloadUrl(data.download_url);
      setStatus('done');
      triggerDownload(data.download_url);
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="text-center py-8" data-testid={uid('success')}>
        <CheckCircle className="w-14 h-14 mx-auto mb-4 text-green-500" />
        <p className="text-lg font-semibold text-slate-800 mb-4">{g.success}</p>
        <Button variant="outline" onClick={() => triggerDownload(downloadUrl)} data-testid={uid('download-again')}>
          <Download className="w-4 h-4 mr-2" />{g.again}
        </Button>
      </div>
    );
  }

  const input = (name, type = 'text', placeholder = '') => (
    <Input id={uid(name)} name={name} type={type} required value={form[name]} onChange={onChange}
      placeholder={placeholder} className="bg-white border-slate-200" data-testid={uid(name)} />
  );

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid={uid('form')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id={uid('first_name')} label={g.fields.first_name}>{input('first_name')}</Field>
        <Field id={uid('last_name')} label={g.fields.last_name}>{input('last_name')}</Field>
        <Field id={uid('email')} label={g.fields.email}>{input('email', 'email', 'nombre@empresa.com')}</Field>
        <Field id={uid('phone')} label={g.fields.phone}>{input('phone', 'tel', '+34 600 000 000')}</Field>
      </div>
      <Field id={uid('website')} label={g.fields.website}>{input('website', 'text', 'www.miempresa.com')}</Field>
      <Field id={uid('reason')} label={g.fields.reason}>
        <Textarea id={uid('reason')} name="reason" required rows={3} value={form.reason} onChange={onChange}
          placeholder={g.reasonPlaceholder} className="bg-white border-slate-200 resize-none" data-testid={uid('reason')} />
      </Field>
      <input type="text" name="hp" value={form.hp} onChange={onChange} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      {language === 'en' && (
        <Field id={uid('pdf-lang')} label={g.fields.pdf_lang}>
          <div className="flex gap-2">
            {[['es', 'Español'], ['fr', 'Français']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setPdfLang(v)} data-testid={uid(`pdf-lang-${v}`)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${pdfLang === v ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-blue-400'}`}>
                {l}
              </button>
            ))}
          </div>
        </Field>
      )}
      <div className="flex items-start space-x-3">
        <Checkbox id={uid('consent')} checked={consent} onCheckedChange={setConsent} className="mt-1" data-testid={uid('consent')} />
        <Label htmlFor={uid('consent')} className="text-sm text-slate-600 leading-relaxed cursor-pointer">
          {g.consent} <a href={g.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{g.privacy}</a>
        </Label>
      </div>
      {status === 'error' && <p className="text-sm text-red-600" data-testid={uid('error')}>{g.error}</p>}
      <Button type="submit" size="lg" disabled={!consent || status === 'loading'} data-testid={uid('submit')}
        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 shadow-lg disabled:opacity-50">
        {status === 'loading' ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
        {g.submit}
      </Button>
    </form>
  );
};

export default GuideDownloadForm;
